import { AlertTriangle, Users, Receipt, Clock, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { KPICard } from '../KPICard';
import { AgingTable } from '../AgingTable';
import { AgingPeriod } from '@/data/dashboardMockData';
import { cn } from '@/lib/utils';

interface Debtor {
  id: string;
  name: string;
  amount: number;
  daysOverdue: number;
}

interface DevedoresTabProps {
  kpiData: {
    totalInadimplente: number;
    clientesAtraso: number;
    ticketMedio: number;
    diasMedioAtraso: number;
  };
  debtors: Debtor[];
  agingData: AgingPeriod[];
  onContactChannel: (debtor: Debtor, channel: 'email' | 'sms' | 'whatsapp') => void;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function DevedoresTab({ kpiData, debtors, agingData, onContactChannel }: DevedoresTabProps) {
  return (
    <div className="space-y-6">
      {/* KPIs de Inadimplência */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Inadimplente"
          value={formatMoney(kpiData.totalInadimplente)}
          variant="careful"
          icon={<AlertTriangle className="h-4 w-4 text-[hsl(var(--careful-600))]" />}
        />
        <KPICard
          title="Clientes em Atraso"
          value={kpiData.clientesAtraso.toString()}
          variant="neutral"
          subtitle="Clientes únicos"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        <KPICard
          title="Ticket Médio"
          value={formatMoney(kpiData.ticketMedio)}
          variant="neutral"
          subtitle="Por cliente"
          icon={<Receipt className="h-4 w-4 text-muted-foreground" />}
        />
        <KPICard
          title="Dias Médio de Atraso"
          value={`${kpiData.diasMedioAtraso}d`}
          variant="neutral"
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      {/* Top Devedores + Aging Vencidas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Devedores com ações de canal */}
        <div className="p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-foreground">Top Devedores</h3>
            <p className="text-xs text-muted-foreground">Ranking por valor em aberto</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left py-2 font-semibold">#</th>
                  <th className="text-left py-2 font-semibold">Cliente</th>
                  <th className="text-right py-2 font-semibold">Valor</th>
                  <th className="text-center py-2 font-semibold">Contato</th>
                </tr>
              </thead>
              <tbody>
                {debtors.map((debtor, index) => (
                  <tr 
                    key={debtor.id} 
                    className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="py-3">
                      <span className={cn(
                        'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold',
                        index === 0 && 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]',
                        index === 1 && 'bg-[hsl(38,92%,90%)] text-[hsl(38,92%,35%)]',
                        index >= 2 && 'bg-muted text-muted-foreground'
                      )}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-3">
                      <div>
                        <p className="text-xs font-medium text-foreground truncate max-w-[150px]">
                          {debtor.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {debtor.daysOverdue}d atraso
                        </p>
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <span className={cn(
                        'text-xs font-bold',
                        debtor.amount >= 10000 
                          ? 'text-[hsl(var(--careful-600))]' 
                          : 'text-foreground'
                      )}>
                        {formatMoney(debtor.amount)}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onContactChannel(debtor, 'email')}
                          className="p-1.5 rounded-lg bg-[hsl(220,70%,95%)] text-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,90%)] transition-colors"
                          title="Enviar Email"
                        >
                          <Mail className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => onContactChannel(debtor, 'sms')}
                          className="p-1.5 rounded-lg bg-[hsl(280,70%,95%)] text-[hsl(280,70%,50%)] hover:bg-[hsl(280,70%,90%)] transition-colors"
                          title="Enviar SMS"
                        >
                          <Smartphone className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => onContactChannel(debtor, 'whatsapp')}
                          className="p-1.5 rounded-lg bg-[hsl(142,70%,90%)] text-[hsl(142,70%,40%)] hover:bg-[hsl(142,70%,85%)] transition-colors"
                          title="Enviar WhatsApp"
                        >
                          <MessageSquare className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tabela Contas a Receber Vencidas com Total */}
        <AgingTable type="overdue" data={agingData} showTotal />
      </div>

      {/* Ações Sugeridas */}
      <div className="p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300">
        <h3 className="text-sm font-semibold text-foreground mb-4">Ações Sugeridas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-gradient-to-br from-[hsl(142,70%,95%)] to-[hsl(142,70%,90%)] border border-[hsl(142,70%,85%)]">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-[hsl(142,70%,40%)]" />
              <span className="text-xs font-semibold text-[hsl(142,70%,30%)]">WhatsApp</span>
            </div>
            <p className="text-sm text-foreground">
              <span className="font-bold">3 clientes</span> respondem melhor por WhatsApp. 
              Priorize esse canal para dívidas urgentes.
            </p>
          </div>
          
          <div className="p-4 rounded-xl bg-gradient-to-br from-[hsl(220,70%,97%)] to-[hsl(220,70%,93%)] border border-[hsl(220,70%,88%)]">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-[hsl(220,70%,50%)]" />
              <span className="text-xs font-semibold text-[hsl(220,70%,35%)]">Email</span>
            </div>
            <p className="text-sm text-foreground">
              <span className="font-bold">2 clientes</span> preferem comunicação formal. 
              Envie boletos atualizados.
            </p>
          </div>
          
          <div className="p-4 rounded-xl bg-gradient-to-br from-[hsl(280,70%,97%)] to-[hsl(280,70%,93%)] border border-[hsl(280,70%,88%)]">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="h-4 w-4 text-[hsl(280,70%,50%)]" />
              <span className="text-xs font-semibold text-[hsl(280,70%,35%)]">SMS</span>
            </div>
            <p className="text-sm text-foreground">
              <span className="font-bold">5 clientes</span> têm alta taxa de abertura de SMS. 
              Use para lembretes rápidos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
